<script setup lang="ts">
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, useAttrs } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import type { ModelValue } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

defineOptions({
  inheritAttrs: false,
  // VueDatePicker本体と同様に、Wrapperのv-modelもVue 3形式で扱う
  compatConfig: {
    MODE: 3,
  },
});

/** VueDatePickerで利用可能な日付表示形式 */
type DatePickerFormat = string | ((date: Date) => string) | ((dates: Date[]) => string);
/** インライン表示の設定 */
type InlineConfig = boolean | { input?: boolean };

const props = withDefaults(
  defineProps<{
    modelValue?: ModelValue;
    uid?: string;
    modelType?: 'timestamp' | 'format' | string;
    format?: DatePickerFormat;
    locale?: string;
    minDate?: Date | string;
    maxDate?: Date | string;
    disabledDates?: string[] | ((date: Date) => boolean) | Date[];
    enableTimePicker?: boolean;
    autoApply?: boolean;
    inline?: InlineConfig;
    teleport?: boolean | string;
    disabled?: boolean;
  }>(),
  {
    modelValue: null,
    uid: undefined,
    modelType: undefined,
    format: undefined,
    locale: undefined,
    minDate: undefined,
    maxDate: undefined,
    disabledDates: undefined,
    enableTimePicker: true,
    autoApply: false,
    inline: false,
    teleport: false,
    disabled: false,
  }
);

const emits = defineEmits<{
  (e: 'update:modelValue', value: ModelValue): void;
}>();

const attrs = useAttrs();
const componentUid = getCurrentInstance()?.uid;
const datePickerUid = computed(() => props.uid ?? `app-date-picker-${componentUid}`);

let focusRepairFrame: number | undefined;

/**
 * 欄外日付の選択で月が切り替わると、同じ位置のDOMが別日付として再利用されて
 * 選択日とキーボードフォーカスがずれることがあるため、選択日へフォーカスを戻す
 */
const repairSelectedDateFocus = async () => {
  if (typeof document === 'undefined') return;

  await nextTick();

  if (focusRepairFrame !== undefined) {
    window.cancelAnimationFrame(focusRepairFrame);
  }

  focusRepairFrame = window.requestAnimationFrame(() => {
    focusRepairFrame = undefined;

    const menu = document.getElementById(`dp-menu-${datePickerUid.value}`);
    const activeElement = document.activeElement;
    const selectedDates = menu?.querySelectorAll<HTMLElement>('.dp__calendar_item[aria-selected="true"]');
    const selectedDate = selectedDates?.length === 1 ? selectedDates[0] : undefined;

    if (
      selectedDate &&
      activeElement instanceof HTMLElement &&
      menu?.contains(activeElement) &&
      activeElement.classList.contains('dp__calendar_item') &&
      activeElement !== selectedDate
    ) {
      selectedDate.focus({ preventScroll: true });
    }
  });
};

onBeforeUnmount(() => {
  if (focusRepairFrame !== undefined) {
    window.cancelAnimationFrame(focusRepairFrame);
  }
});
</script>

<template>
  <VueDatePicker
    v-bind="attrs"
    :uid="datePickerUid"
    :model-value="modelValue"
    :model-type="modelType"
    :format="format"
    :locale="locale"
    :min-date="minDate"
    :max-date="maxDate"
    :disabled-dates="disabledDates"
    :enable-time-picker="enableTimePicker"
    :auto-apply="autoApply"
    :inline="inline"
    :teleport="teleport"
    :disabled="disabled"
    @update:model-value="emits('update:modelValue', $event)"
    @internal-model-change="repairSelectedDateFocus"
  >
    <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
      <slot :name="slotName" v-bind="slotProps" />
    </template>
  </VueDatePicker>
</template>
